<template>
  <div>
    <div class="shopping-cart" :class="{ 'is-active': is_visible }">
      <!-- Header del carrito mejorado -->
      <div class="cart-header">
        <div class="header-content">
          <div class="cart-title">
            <i class="fa fa-shopping-cart cart-icon"></i>
            <h3 class="title is-4 mb-0">
              {{ $t("your cart") }}
            </h3>
          </div>
          <span class="cart-count"
            >{{ cart_items.length }}
            {{ cart_items.length === 1 ? "item" : "items" }}</span
          >
        </div>
        <button class="close-button" @click="hide_cart">
          <i class="fa fa-times"></i>
        </button>
      </div>

      <!-- Contenido del carrito -->
      <div class="cart-content">
        <!-- Estado vacío mejorado -->
        <div v-if="cart_items.length === 0" class="empty-cart">
          <div class="empty-cart-icon">
            <i class="fa fa-shopping-cart"></i>
          </div>
          <h4 class="empty-title">
            {{ $t("your cart is empty") || "Tu carrito está vacío" }}
          </h4>
          <p class="empty-subtitle">
            {{
              $t("add products to continue") ||
              "Agrega productos para continuar"
            }}
          </p>
          <button
            class="button is-primary is-rounded continue-shopping-btn"
            @click="hide_cart"
          >
            <span class="icon">
              <i class="fa fa-arrow-left"></i>
            </span>
            <span>{{ $t("continue shopping") || "Continuar comprando" }}</span>
          </button>
        </div>

        <!-- Lista de productos mejorada -->
        <div v-else class="cart-items">
          <div
            class="cart-item"
            v-for="item in cart_items"
            :key="item.product_id"
          >
            <div class="item-image">
              <figure class="image is-64x64">
                <v-lazy-image
                  v-if="item.image"
                  :src="`${api_host}/drive/static/${item.image}`"
                  :alt="item.name"
                  class="product-thumbnail"
                />
                <div v-else class="image-placeholder">
                  <i class="fa fa-image"></i>
                </div>
              </figure>
            </div>

            <div class="item-details">
              <h5 class="item-name">{{ item.name }}</h5>
              <p class="item-price-unit">
                {{ format_currency(item.price.toFixed(2)) }} c/u
              </p>

              <div class="quantity-controls">
                <button
                  class="quantity-btn decrease"
                  @click="update_quantity(item.product_id, -1)"
                  :disabled="item.quantity <= 1"
                >
                  <i class="fa fa-minus"></i>
                </button>
                <span class="quantity-display">{{ item.quantity }}</span>
                <button
                  class="quantity-btn increase"
                  @click="update_quantity(item.product_id, 1)"
                >
                  <i class="fa fa-plus"></i>
                </button>
              </div>
            </div>

            <div class="item-actions">
              <div class="item-total">
                {{
                  format_currency(
                    (Number(item.quantity) * Number(item.price)).toFixed(2)
                  )
                }}
              </div>
              <button
                class="remove-btn"
                @click="remove_from_cart(item.product_id)"
                :title="$t('remove from cart') || 'Eliminar del carrito'"
              >
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer del carrito mejorado -->
      <div class="cart-footer" v-if="cart_items.length > 0">
        <div class="cart-summary">
          <div class="summary-row subtotal">
            <span>{{ $t("subtotal") || "Subtotal" }}:</span>
            <span>{{ format_currency(cart_total.toFixed(2)) }}</span>
          </div>
          <div class="summary-row total">
            <span>{{ $t("total") || "Total" }}:</span>
            <span class="total-amount">{{
              format_currency(cart_total.toFixed(2))
            }}</span>
          </div>
        </div>

        <div class="checkout-actions">
          <button class="button is-dark is-fullwidth mb-2" @click="hide_cart">
            <span class="icon">
              <i class="fa fa-arrow-left"></i>
            </span>
            <span>{{ $t("continue shopping") || "Continuar comprando" }}</span>
          </button>
          <button
            class="button is-success is-fullwidth checkout-btn"
            @click="checkout"
          >
            <span class="icon">
              <i class="fa fa-credit-card"></i>
            </span>
            <span>{{ $t("checkout") || "Finalizar compra" }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- Overlay mejorado -->
    <div
      class="cart-overlay"
      :class="{ 'is-active': is_visible }"
      @click="hide_cart"
    ></div>
  </div>
</template>

<script>
import VLazyImage from "v-lazy-image";
import { api_host, format_currency } from "@/utils";

export default {
  name: "Cart",
  components: {
    VLazyImage,
  },
  data() {
    return {
      api_host,
      is_visible: false,
      cart_items: [],
    };
  },
  computed: {
    cart_total() {
      return this.cart_items.reduce((total, item) => {
        return total + item.price * item.quantity;
      }, 0);
    },
  },
  methods: {
    format_currency,
    hide_cart() {
      let v = this;
      v.is_visible = false;
      v.cart_items = [];
    },
    toggle_cart(cart_items) {
      let v = this;
      if (cart_items && cart_items.length) v.cart_items = cart_items;
      v.is_visible = !v.is_visible;
    },
    update_quantity(itemId, change) {
      const item = this.cart_items.find((item) => item.product_id === itemId);
      if (item) {
        item.quantity += change;
        if (item.quantity < 1) {
          item.quantity = 1;
        }
        this.$emit("cart_updated", this.cart_items);
      }
    },
    remove_from_cart(itemId) {
      this.cart_items = this.cart_items.filter(
        (item) => item.product_id !== itemId
      );
      this.$emit("cart_updated", this.cart_items);
    },
    checkout() {
      this.$emit("payment", this.cart_items);
      this.hide_cart();
    },
  },
  watch: {},
};
</script>

<style scoped>
/* Variables CSS para el tema del carrito */
:root {
  --cart-primary: #667eea;
  --cart-success: #4ecdc4;
  --cart-danger: #ff6b6b;
  --cart-warning: #feca57;
  --cart-dark: #2c3e50;
  --cart-light: #f8f9fa;
  --cart-border: #e9ecef;
  --cart-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  --cart-shadow-light: 0 2px 10px rgba(0, 0, 0, 0.05);
  --cart-radius: 12px;
  --cart-transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Contenedor principal del carrito */
.shopping-cart {
  position: fixed;
  top: 0;
  right: -450px;
  width: 450px;
  height: 100vh;
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  box-shadow: -10px 0 30px rgba(0, 0, 0, 0.2);
  z-index: 40;
  transition: var(--cart-transition);
  display: flex;
  flex-direction: column;
  border-radius: var(--cart-radius) 0 0 var(--cart-radius);
}

.shopping-cart.is-active {
  right: 0;
}

/* Header del carrito */
.cart-header {
  background: linear-gradient(135deg, var(--cart-primary) 0%, #764ba2 100%);
  color: white;
  padding: 2rem 1.5rem 1.5rem;
  border-radius: var(--cart-radius) 0 0 0;
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.header-content {
  flex: 1;
}

.cart-title {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.cart-icon {
  font-size: 1.5rem;
  margin-right: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
}

.cart-title .title {
  color: rgb(37, 37, 37) !important;
  margin: 0;
}

.cart-count {
  font-size: 0.9rem;
  color: rgb(37, 37, 37) !important;
  opacity: 0.8;
  font-weight: 500;
}

.close-button {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: var(--cart-transition);
  backdrop-filter: blur(10px);
}

.close-button:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Contenido del carrito */
.cart-content {
  flex: 1;
  overflow-y: auto;
  padding: 0;
}

/* Estado vacío */
.empty-cart {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  height: 100%;
}

.empty-cart-icon {
  font-size: 4rem;
  color: #cbd5e0;
  margin-bottom: 1.5rem;
  opacity: 0.6;
}

.empty-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--cart-dark);
  margin-bottom: 0.5rem;
}

.empty-subtitle {
  color: #718096;
  margin-bottom: 2rem;
  font-size: 0.95rem;
}

.continue-shopping-btn {
  background: linear-gradient(
    135deg,
    var(--cart-primary) 0%,
    #764ba2 100%
  ) !important;
  border: none !important;
  font-weight: 600;
  padding: 0.75rem 2rem;
  transition: var(--cart-transition);
}

.continue-shopping-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--cart-shadow-light);
}

/* Lista de productos */
.cart-items {
  padding: 1rem 0;
}

.cart-item {
  display: flex;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--cart-border);
  transition: var(--cart-transition);
  position: relative;
}

.cart-item:hover {
  background-color: #f8f9fa;
}

.cart-item:last-child {
  border-bottom: none;
}

/* Imagen del producto */
.item-image {
  margin-right: 1rem;
  flex-shrink: 0;
}

.product-thumbnail {
  width: 64px;
  height: 64px;
  object-fit: cover;
  border-radius: 8px;
  box-shadow: var(--cart-shadow-light);
}

.image-placeholder {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #a0aec0;
  font-size: 1.5rem;
}

/* Detalles del producto */
.item-details {
  flex: 1;
  min-width: 0;
}

.item-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--cart-dark);
  margin-bottom: 0.25rem;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.item-price-unit {
  font-size: 0.85rem;
  color: #718096;
  margin-bottom: 0.75rem;
}

/* Controles de cantidad */
.quantity-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.quantity-btn {
  width: 28px;
  height: 28px;
  border: 1px solid var(--cart-border);
  background: white;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--cart-transition);
  font-size: 0.8rem;
  color: var(--cart-dark);
}

.quantity-btn:hover:not(:disabled) {
  background: var(--cart-primary);
  color: white;
  border-color: var(--cart-primary);
}

.quantity-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.quantity-display {
  min-width: 40px;
  text-align: center;
  font-weight: 600;
  color: var(--cart-dark);
  font-size: 0.95rem;
}

/* Acciones del item */
.item-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.5rem;
  margin-left: 1rem;
}

.item-total {
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--cart-dark);
  text-align: right;
}

.remove-btn {
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.3);
  color: var(--cart-danger);
  border-radius: 6px;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: var(--cart-transition);
  font-size: 0.85rem;
}

.remove-btn:hover {
  background: var(--cart-danger);
  color: white;
  border-color: var(--cart-danger);
}

/* Footer del carrito */
.cart-footer {
  background: white;
  padding: 1.5rem;
  border-top: 1px solid var(--cart-border);
  box-shadow: 0 -5px 15px rgba(0, 0, 0, 0.05);
}

.cart-summary {
  margin-bottom: 1.5rem;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
}

.summary-row.subtotal {
  color: #718096;
  font-size: 0.95rem;
  border-bottom: 1px solid var(--cart-border);
  margin-bottom: 0.5rem;
  padding-bottom: 0.75rem;
}

.summary-row.total {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--cart-dark);
}

.total-amount {
  color: var(--cart-primary);
  font-size: 1.4rem;
}

/* Botones de acción */
.checkout-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.checkout-btn {
  background: linear-gradient(
    135deg,
    var(--cart-success) 0%,
    #44a08d 100%
  ) !important;
  border: none !important;
  font-weight: 600;
  padding: 1rem;
  transition: var(--cart-transition);
  font-size: 1.05rem;
}

.checkout-btn:hover {
  transform: translateY(-2px);
  box-shadow: var(--cart-shadow);
}

/* Overlay */
.cart-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(2px);
  z-index: 30;
  opacity: 0;
  visibility: hidden;
  transition: var(--cart-transition);
}

.cart-overlay.is-active {
  opacity: 1;
  visibility: visible;
}

/* Animaciones */
@keyframes slideInFromRight {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.cart-item {
  animation: fadeInUp 0.3s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media screen and (max-width: 768px) {
  .shopping-cart {
    width: 100%;
    right: -100%;
    border-radius: 0;
  }

  .cart-header {
    border-radius: 0;
    padding: 1.5rem 1rem 1rem;
  }

  .cart-item {
    padding: 1rem;
  }

  .item-details {
    margin-right: 0.5rem;
  }

  .item-actions {
    margin-left: 0.5rem;
  }
}

@media screen and (max-width: 480px) {
  .item-name {
    font-size: 0.9rem;
  }

  .quantity-controls {
    gap: 0.25rem;
  }

  .quantity-btn {
    width: 24px;
    height: 24px;
    font-size: 0.7rem;
  }

  .quantity-display {
    min-width: 30px;
    font-size: 0.85rem;
  }
}

/* Scrollbar personalizado */
.cart-content::-webkit-scrollbar {
  width: 6px;
}

.cart-content::-webkit-scrollbar-track {
  background: var(--cart-light);
}

.cart-content::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.cart-content::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}
</style>
